import { useTranslation } from 'react-i18next';
import { JOB_CATEGORY_LABELS, normalizeJobCategory } from '@/constants/jobCategories';

const STATUS_STYLES = {
  draft: { bg: 'bg-slate-100', text: 'text-slate-700', dot: 'bg-slate-400' },
  unscheduled: { bg: 'bg-orange-50', text: 'text-orange-700', dot: 'bg-orange-500' },
  sent: { bg: 'bg-blue-50', text: 'text-blue-700', dot: 'bg-blue-500' },
  viewed: { bg: 'bg-sky-50', text: 'text-sky-700', dot: 'bg-sky-500' },
  accepted: { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  approved: { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  rejected: { bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-500' },
  denied: { bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-500' },
  pending: { bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500' },
  completed: { bg: 'bg-green-50', text: 'text-green-800', dot: 'bg-green-600' },
  in_progress: { bg: 'bg-blue-50', text: 'text-blue-700', dot: 'bg-blue-500' },
  scheduled: { bg: 'bg-indigo-50', text: 'text-indigo-700', dot: 'bg-indigo-500' },
  cancelled: { bg: 'bg-gray-100', text: 'text-gray-500 line-through', dot: 'bg-gray-400' },
  overdue: { bg: 'bg-red-100', text: 'text-red-800', dot: 'bg-red-600' },
  converted_to_job: { bg: 'bg-violet-50', text: 'text-violet-700', dot: 'bg-violet-500' },
  converted_to_invoice: { bg: 'bg-blue-50', text: 'text-blue-700', dot: 'bg-blue-600' },
  on_hold: { bg: 'bg-yellow-50', text: 'text-yellow-700', dot: 'bg-yellow-500' },
  invoiced: { bg: 'bg-teal-50', text: 'text-teal-700', dot: 'bg-teal-500' },
  expired: { bg: 'bg-gray-100', text: 'text-gray-500', dot: 'bg-gray-400' },
  new: { bg: 'bg-cyan-50', text: 'text-cyan-700', dot: 'bg-cyan-500' },
  paid: { bg: 'bg-green-50', text: 'text-green-800', dot: 'bg-green-600' },
  partial: { bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500' },
  active: { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  finalized: { bg: 'bg-indigo-50', text: 'text-indigo-700', dot: 'bg-indigo-500' },
  converted: { bg: 'bg-purple-50', text: 'text-purple-700', dot: 'bg-purple-500' },
  quoted: { bg: 'bg-indigo-50', text: 'text-indigo-700', dot: 'bg-indigo-500' },
  lost: { bg: 'bg-rose-50', text: 'text-rose-600', dot: 'bg-rose-500' },
  contacted: { bg: 'bg-blue-50', text: 'text-blue-700', dot: 'bg-blue-500' },
  maintenance: { bg: 'bg-orange-50', text: 'text-orange-700', dot: 'bg-orange-500' },
  retired: { bg: 'bg-gray-100', text: 'text-gray-500', dot: 'bg-gray-400' },
  submitted: { bg: 'bg-blue-50', text: 'text-blue-700', dot: 'bg-blue-500' },
  submitted_to_county: { bg: 'bg-blue-50', text: 'text-blue-700', dot: 'bg-blue-500' },
  needs_more_info: { bg: 'bg-orange-50', text: 'text-orange-700', dot: 'bg-orange-500' },
  approved_by_county: { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  pending_customer_info: { bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500' },
  abandoned: { bg: 'bg-gray-100', text: 'text-gray-500', dot: 'bg-gray-400' },
  // Well Construction sub-statuses
  in_route: { bg: 'bg-sky-50', text: 'text-sky-700', dot: 'bg-sky-500' },
  mast_up: { bg: 'bg-orange-50', text: 'text-orange-700', dot: 'bg-orange-500' },
  mast_down: { bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500' },
  well_log: { bg: 'bg-purple-50', text: 'text-purple-700', dot: 'bg-purple-500' },
  in_dispute: { bg: 'bg-rose-50', text: 'text-rose-700', dot: 'bg-rose-600' },
  // Permit-job pipeline (their own 6-step flow)
  granted: { bg: 'bg-cyan-50', text: 'text-cyan-700', dot: 'bg-cyan-500' },
  sent_to_customer: { bg: 'bg-violet-50', text: 'text-violet-700', dot: 'bg-violet-500' },
};

const PRIORITY_STYLES = {
  low: { bg: 'bg-slate-100', text: 'text-slate-600', dot: 'bg-slate-400' },
  normal: { bg: 'bg-blue-50', text: 'text-blue-600', dot: 'bg-blue-400' },
  medium: { bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500' },
  high: { bg: 'bg-orange-50', text: 'text-orange-700', dot: 'bg-orange-500' },
  urgent: { bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-500' },
};

export function StatusBadge({ status, className = '' }) {
  const { t } = useTranslation();
  const styles = STATUS_STYLES[status] || { bg: 'bg-gray-100', text: 'text-gray-600', dot: 'bg-gray-400' };
  const label = t(`statusBadges.${status}`, status);
  
  return (
    <span
      data-testid={`status-badge-${status}`}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold tracking-wide uppercase ${styles.bg} ${styles.text} ${className}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${styles.dot}`} />
      {label}
    </span>
  );
}

export function PriorityBadge({ priority, className = '' }) {
  const { t } = useTranslation();
  const styles = PRIORITY_STYLES[priority] || { bg: 'bg-gray-100', text: 'text-gray-600', dot: 'bg-gray-400' };
  const label = t(`jobs.priorities.${priority}`, priority);
  
  return (
    <span
      data-testid={`priority-badge-${priority}`}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold tracking-wide uppercase ${styles.bg} ${styles.text} ${className}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${styles.dot}`} />
      {label}
    </span>
  );
}

export function TypeBadge({ type }) {
  // Normalize legacy values (e.g. new_construction, pump_install) to the
  // canonical job categories so old records still display a proper label.
  const label = JOB_CATEGORY_LABELS[normalizeJobCategory(type)] || type;
  
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-slate-100 text-slate-600">
      {label}
    </span>
  );
}
